import React from 'react';
import { useTranslation } from 'react-i18next';
import { Truck, Clock, MapPin, CreditCard, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Shipping = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const content = {
    en: {
      title: "SHIPPING & DELIVERY",
      freeShipping: "FREE SHIPPING on orders over €50!",
      zones: [
        {
          zone: "France & Spain",
          flag: "🇫🇷 🇪🇸",
          cost: "€4.99",
          time: "3-5 business days",
          free: "Free over €50"
        },
        {
          zone: "European Union",
          flag: "🇪🇺",
          cost: "€7.99",
          time: "5-10 business days",
          free: "Free over €50"
        }
      ],
      info: [
        {
          title: "Processing Time",
          icon: Clock,
          content: "Orders are processed within 1-2 business days. You will receive a confirmation email once your order is shipped."
        },
        {
          title: "Tracking",
          icon: Package,
          content: "All orders include tracking. You will receive tracking information via email once your package is shipped."
        },
        {
          title: "Delivery",
          icon: Truck,
          content: "Packages are delivered to the address provided at checkout. Make sure to provide accurate delivery information. We are not responsible for packages delivered to incorrect addresses."
        },
        {
          title: "Customs & Duties",
          icon: CreditCard,
          content: "For EU countries, no customs duties apply. All prices include applicable VAT."
        }
      ],
      restrictions: "Important: We only ship to France, Spain, and EU countries. We do not ship to UK, Switzerland, or non-EU countries at this time.",
      discretePackaging: "All orders are shipped in discrete, unmarked packaging for your privacy."
    },
    es: {
      title: "ENVÍO Y ENTREGA",
      freeShipping: "¡ENVÍO GRATIS en pedidos de más de 50€!",
      zones: [
        {
          zone: "Francia y España",
          flag: "🇫🇷 🇪🇸",
          cost: "4,99€",
          time: "3-5 días laborables",
          free: "Gratis a partir de 50€"
        },
        {
          zone: "Unión Europea",
          flag: "🇪🇺",
          cost: "7,99€",
          time: "5-10 días laborables",
          free: "Gratis a partir de 50€"
        }
      ],
      info: [
        {
          title: "Tiempo de Procesamiento",
          icon: Clock,
          content: "Los pedidos se procesan en 1-2 días laborables. Recibirás un email de confirmación cuando tu pedido sea enviado."
        },
        {
          title: "Seguimiento",
          icon: Package,
          content: "Todos los pedidos incluyen seguimiento. Recibirás la información de seguimiento por email cuando tu paquete sea enviado."
        },
        {
          title: "Entrega",
          icon: Truck,
          content: "Los paquetes se entregan en la dirección proporcionada en el checkout. Asegúrate de proporcionar información de entrega correcta. No somos responsables de paquetes entregados en direcciones incorrectas."
        },
        {
          title: "Aduanas e Impuestos",
          icon: CreditCard,
          content: "Para países de la UE, no se aplican aranceles aduaneros. Todos los precios incluyen IVA aplicable."
        }
      ],
      restrictions: "Importante: Solo enviamos a Francia, España y países de la UE. No enviamos a Reino Unido, Suiza ni países fuera de la UE en este momento.",
      discretePackaging: "Todos los pedidos se envían en embalaje discreto y sin marcas para tu privacidad."
    },
    fr: {
      title: "LIVRAISON",
      freeShipping: "LIVRAISON GRATUITE pour les commandes de plus de 50€ !",
      zones: [
        {
          zone: "France et Espagne",
          flag: "🇫🇷 🇪🇸",
          cost: "4,99€",
          time: "3-5 jours ouvrés",
          free: "Gratuit dès 50€"
        },
        {
          zone: "Union Européenne",
          flag: "🇪🇺",
          cost: "7,99€",
          time: "5-10 jours ouvrés",
          free: "Gratuit dès 50€"
        }
      ],
      info: [
        {
          title: "Délai de Traitement",
          icon: Clock,
          content: "Les commandes sont traitées sous 1-2 jours ouvrés. Vous recevrez un e-mail de confirmation une fois votre commande expédiée."
        },
        {
          title: "Suivi",
          icon: Package,
          content: "Toutes les commandes incluent le suivi. Vous recevrez les informations de suivi par e-mail une fois votre colis expédié."
        },
        {
          title: "Livraison",
          icon: Truck,
          content: "Les colis sont livrés à l'adresse fournie lors du paiement. Assurez-vous de fournir des informations de livraison exactes. Nous ne sommes pas responsables des colis livrés à des adresses incorrectes."
        },
        {
          title: "Douanes et Taxes",
          icon: CreditCard,
          content: "Pour les pays de l'UE, aucun droit de douane ne s'applique. Tous les prix incluent la TVA applicable."
        }
      ],
      restrictions: "Important : Nous livrons uniquement en France, Espagne et pays de l'UE. Nous ne livrons pas au Royaume-Uni, en Suisse ou dans les pays hors UE pour le moment.",
      discretePackaging: "Toutes les commandes sont expédiées dans un emballage discret et sans marque pour votre confidentialité."
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="shipping-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <Truck className="w-12 h-12 text-[#FF0099] mx-auto mb-4" />
          <h1 className="font-heading text-4xl text-white font-bold mb-4">{c.title}</h1>
          <div className="inline-block bg-[#FAFF00] text-black font-bold px-6 py-2 transform -rotate-1">
            {c.freeShipping}
          </div>
        </div>

        {/* Shipping Zones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {c.zones.map((zone, index) => (
            <div key={index} className="bg-[#1A1A1A] border border-[#333333] p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{zone.flag}</span>
                <h3 className="font-heading text-white font-bold">{zone.zone}</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Cost:</span>
                  <span className="text-white font-bold">{zone.cost}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Delivery:</span>
                  <span className="text-white">{zone.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#A3A3A3]">Free shipping:</span>
                  <span className="text-[#00FFFF] font-bold">{zone.free}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="space-y-6 mb-12">
          {c.info.map((item, index) => (
            <div key={index} className="bg-[#1A1A1A] border border-[#333333] p-6 flex gap-4">
              <item.icon className="w-6 h-6 text-[#FF0099] shrink-0 mt-1" />
              <div>
                <h3 className="font-heading text-white font-bold mb-2">{item.title}</h3>
                <p className="text-[#A3A3A3] text-sm">{item.content}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Discrete Packaging Notice */}
        <div className="bg-[#FF0099]/10 border border-[#FF0099]/30 p-6 text-center mb-8">
          <p className="text-white font-bold">{c.discretePackaging}</p>
        </div>

        {/* Restrictions */}
        <div className="bg-[#1A1A1A] border border-[#FAFF00]/30 p-6">
          <p className="text-[#FAFF00] text-sm">{c.restrictions}</p>
        </div>

        <div className="mt-12 text-center">
          <Link to="/shop" className="text-[#FF0099] hover:text-[#FAFF00] transition-colors text-sm">
            ← Back to Shop
          </Link>
        </div>
      </div>
    </div>
  );
};
